/**
 * useSmsFeed — the inbound-text half of the overlay manager.
 *
 * InboundSmsToastHost's semantics, unchanged: subscribe once to the fixed
 * ids-only `crm:inbound-sms` topic, apply the client-side visibility gate
 * (recruiters see unassigned leads and their own; admins see all — the server
 * re-checks and a 403 is silently ignored), then fetch the preview through the
 * authed, RLS-aware API before anything is shown. Each (contactId, occurredAt)
 * is toasted at most once, ever.
 *
 * Two differences, both the point of D-2: the toasts move from the bottom-LEFT
 * corner into the one bottom-right stack, and the four-toast cap is gone. v1
 * capped by DROPPING the oldest — a text a recruiter had not read yet vanished
 * because a newer one arrived. Here the overflow queues, and the 15s dismissal
 * budget belongs to the queue, which only spends it while the card is on screen.
 *
 * OUTREACH LISTS (docs/crm-outreach-lists-plan.md §B7): a reply from someone on
 * an open outreach list is the most valuable text of the day — the list exists
 * to get exactly that. The broadcast stays ids-only; the preview endpoint adds
 * `outreach_list_name`, and a list reply (1) reads "Reply on <list name>" on
 * the card, (2) raises an OS notification even when the tab is not in front,
 * and (3) when the lead is MINE, pokes a running list session to re-check at
 * once so its "new reply — up next" chip does not wait out the minute.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { subscribeInboundSms } from '../../../lib/realtimeTransport';
import { useAuth } from '../../../contexts/AuthContext';
import { OUTREACH_REPLY_EVENT } from '../../workQueueLogic';
import { fireOsNote } from './alertKinds';

export interface SmsCard {
  /** contactId + occurredAt — also the forever de-dupe key. */
  id: string;
  contactId: string;
  name: string | null;
  preview: string | null;
  occurredAt: string | null;
  /** Set when the texter is an open member of an outreach list. */
  listName?: string | null;
}

interface InboundPreview {
  contact_id: string;
  name: string | null;
  preview: string | null;
  owner_user_id: string | null;
  occurred_at: string | null;
  /** The open outreach list they are on (null/absent = none, or an older api). */
  outreach_list_name?: string | null;
}

export interface SmsFeed {
  cards: SmsCard[];
  dismiss: (id: string) => void;
  open: (card: SmsCard) => void;
}

export function useSmsFeed(): SmsFeed {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState<SmsCard[]>([]);
  // Every (contactId + occurredAt) we've ever shown, so a missed broadcast or a
  // re-render never double-toasts the same message.
  const shownKeysRef = useRef<Set<string>>(new Set());

  // Keep the latest user in a ref so the (stable, once-only) subscription's
  // callback can read the current id/role without resubscribing per render.
  const userRef = useRef(user);
  userRef.current = user;

  const dismiss = useCallback((id: string) => {
    setCards((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const open = useCallback((card: SmsCard) => {
    dismiss(card.id);
    navigate(`/crm/contacts/${card.contactId}`);
  }, [dismiss, navigate]);

  useEffect(() => {
    const unsubscribe = subscribeInboundSms(async (payload) => {
      const contactId = payload.contactId;
      if (!contactId) return;

      // Client-side visibility gate (the server re-checks; this just avoids a
      // pointless fetch). Recruiters only see unassigned / own leads.
      const u = userRef.current;
      const ownerUserId = payload.ownerUserId ?? null;
      const canSee =
        u?.role === 'super_admin' ||
        u?.role === 'administrator' ||
        ownerUserId == null ||
        ownerUserId === u?.id;
      if (!canSee) return;

      try {
        const res = await api.get<InboundPreview>(`/api/crm/contacts/${contactId}/inbound-preview`);
        const data = res.data;
        if (!data) return;
        const key = `${data.contact_id}:${data.occurred_at ?? ''}`;
        if (shownKeysRef.current.has(key)) return;
        shownKeysRef.current.add(key);
        const listName = typeof data.outreach_list_name === 'string' && data.outreach_list_name.trim()
          ? data.outreach_list_name.trim()
          : null;
        // No cap and no eviction: the stack queues the overflow instead.
        setCards((prev) => [...prev, {
          id: key,
          contactId: data.contact_id,
          name: data.name,
          preview: data.preview,
          occurredAt: data.occurred_at,
          listName,
        }]);
        if (listName) {
          // Name only — never the message text: an OS notification can sit on
          // a lock screen, and a patient's reply may carry health details.
          fireOsNote({
            title: `Reply on ${listName}`,
            body: `${data.name || 'A lead'} texted back`,
            tag: `crm-sms:${key}`,
          });
          if (data.owner_user_id && data.owner_user_id === userRef.current?.id) {
            window.dispatchEvent(new CustomEvent(OUTREACH_REPLY_EVENT, { detail: { contactId: data.contact_id } }));
          }
        }
      } catch {
        /* 403 (not visible) or any error — silently ignore, never toast */
      }
    });
    return unsubscribe;
  }, []);

  return { cards, dismiss, open };
}

export default useSmsFeed;
